import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Loader2, Building2, Car, Truck, Landmark } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const leadTypes = [
  { id: 'healthcare_provider', label: 'Healthcare Provider', icon: Building2 },
  { id: 'driver', label: 'Driver', icon: Car },
  { id: 'transportation_company', label: 'Transportation Company', icon: Truck },
  { id: 'government', label: 'City / Government', icon: Landmark },
  { id: 'partner', label: 'Partner / Investor', icon: Building2 },
];

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    lead_type: '',
    message: '',
    sms_opt_in: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.lead_type) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/leads`, formData);
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="glass-card p-8 text-center"
        data-testid="contact-success"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-16 h-16 rounded-full bg-neon-green/20 flex items-center justify-center mx-auto mb-6"
        >
          <CheckCircle className="w-8 h-8 text-neon-green" />
        </motion.div>
        <h3 className="text-2xl font-heading font-bold text-white mb-3">
          Thank You!
        </h3>
        <p className="text-slate-400 mb-6">
          We've received your information and will be in touch within 24 hours.
        </p>
        <button
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              name: '',
              email: '',
              company: '',
              phone: '',
              lead_type: '',
              message: '',
              sms_opt_in: false
            });
          }}
          className="btn-secondary"
        >
          Submit Another Request
        </button>
      </motion.div>
    );
  }

  return (
    <section 
      id="contact" 
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-medex-bg via-medex-surface/20 to-medex-bg" />
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-1 rounded-full bg-white/5 border border-white/10 text-sm font-mono text-slate-400 uppercase tracking-wider mb-6">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
            Join the Network
          </h2>
          <p className="max-w-2xl mx-auto text-lg text-slate-400">
            Ready to transform your healthcare transportation? Let's talk about how MedexOVA can help.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onSubmit={handleSubmit}
          className="glass-card p-6 lg:p-8"
          data-testid="contact-form"
        >
          {/* Lead Type Selection */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-slate-300 mb-4">
              I am a... <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {leadTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, lead_type: type.id }))}
                  className={`
                    p-4 rounded-xl border transition-all duration-300 text-center
                    ${formData.lead_type === type.id 
                      ? 'bg-neon-cyan/10 border-neon-cyan/50 text-neon-cyan' 
                      : 'bg-white/5 border-white/10 text-slate-400 hover:border-white/20'}
                  `}
                  data-testid={`lead-type-${type.id}`}
                >
                  <type.icon className="w-5 h-5 mx-auto mb-2" />
                  <span className="text-xs">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full input-dark"
                placeholder="John Smith"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full input-dark"
                placeholder="john@company.com"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Company / Organization
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full input-dark"
                placeholder="Acme Healthcare"
                data-testid="contact-company"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Phone Number <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full input-dark"
                placeholder="+1 (555) 000-0000"
                data-testid="contact-phone"
              />
            </div>
          </div>

          {/* SMS Opt-In Section - Required for Brevo Toll-Free Compliance */}
          <div className="mb-6 p-4 rounded-xl bg-white/5 border border-neon-cyan/20">
            <label className="block text-sm font-semibold text-white mb-3">
              Sign up for SMS:
            </label>
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                id="sms_opt_in"
                name="sms_opt_in"
                checked={formData.sms_opt_in}
                onChange={(e) => setFormData(prev => ({ ...prev, sms_opt_in: e.target.checked }))}
                className="mt-1 w-5 h-5 rounded border-white/20 bg-white/5 text-neon-cyan focus:ring-neon-cyan/50 cursor-pointer"
                data-testid="sms-opt-in-checkbox"
              />
              <label htmlFor="sms_opt_in" className="text-sm text-slate-300 cursor-pointer leading-relaxed">
                <span className="font-medium text-white">I agree to receive automated SMS text messages from MedexOVA</span> at the phone number provided above regarding service updates, appointment reminders, and important notifications. 
                <span className="block mt-2 text-xs text-slate-400">
                  Message frequency may vary. Standard message and data rates may apply. Reply <span className="font-semibold text-neon-cyan">STOP</span> to opt out. Reply <span className="font-semibold text-neon-cyan">HELP</span> for assistance. Your mobile information will not be sold or shared with third parties for promotional or marketing purposes. View our{' '}
                  <a href="/privacy" className="text-neon-cyan hover:underline">Privacy Policy</a> and{' '}
                  <a href="/terms" className="text-neon-cyan hover:underline">Terms of Service</a>.
                </span>
              </label>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full input-dark resize-none"
              placeholder="Tell us about your needs..."
              data-testid="contact-message"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="contact-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>Get Started</span>
                <Send className="w-5 h-5" />
              </>
            )}
          </button>

          <p className="mt-4 text-center text-xs text-slate-500">
            By submitting, you agree to our privacy policy. We'll never share your information.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
